import { apiClient } from './client';

/**
 * AI 대화 보관 — 목록 · 불러오기 · 삭제 (설계 판 C).
 *
 * ★ 한 턴 보내기(`api/assist.ts`)는 스트림이라 `fetch` 를 쓰지만, 여기는 평범한 JSON 이라
 *   공용 `apiClient`(쿠키·재발급·트레이스 헤더)를 그대로 쓴다.
 */

export interface AiConversationSummary {
  id: string;
  title: string;
  /** ISO — 「오늘 · 어제 · 지난 7일」로 나누는 것은 화면이 한다. */
  lastMessageAt: string;
  /** 이 대화를 이끈 에이전트(판 E). null = 에이전트 없이 물은 대화. */
  agentId?: string | null;
}

export interface AiConversationDetail extends AiConversationSummary {
  messages: Array<{ id: string; role: 'user' | 'assistant'; text: string }>;
}

export async function listAiConversations(limit = 30): Promise<AiConversationSummary[]> {
  const res = await apiClient.get<{ items: AiConversationSummary[] }>('/assist/conversations', {
    params: { limit },
  });
  return res.data.items ?? [];
}

export async function getAiConversation(id: string): Promise<AiConversationDetail> {
  const res = await apiClient.get<AiConversationDetail>(`/assist/conversations/${id}`);
  return res.data;
}

/** ⚠ 되돌릴 수 없다 — 부르는 쪽이 **먼저** 확인을 받는다(휴지통을 두지 않기로 했다). */
/**
 * 대화 이름 바꾸기 (2026-09-11).
 *
 * ★ 제목은 첫 질문에서 따 오지만, 물음이 짧거나 여러 주제로 번진 대화는 목록에서 구별이 안 된다.
 *   그때 사람이 고쳐 적을 수 있어야 한다.
 */
export async function renameAiConversation(id: string, title: string): Promise<string> {
  const { data } = await apiClient.patch<{ id: string; title: string }>(
    `/assist/conversations/${id}`, { title },
  );
  return data.title;
}

export async function deleteAiConversation(id: string): Promise<void> {
  await apiClient.delete(`/assist/conversations/${id}`);
}

export interface AiModelOptions {
  /** 배포가 정해 둔 별칭(관문 G2). 안 정해 뒀으면 빈 배열. */
  aliases: string[];
  /** 판 K — 회사가 지정한 연결들. `null` = 못 읽었다(「고를 것이 없다」와 다르다). */
  choices: Array<{ key: string; label: string }> | null;
  defaultKey: string | null;
}

/**
 * 이 사람이 대화에서 **실제로 고를 수 있는 것** (관문 G2 별칭 + 판 K 회사 지정 연결).
 *
 * ★ 별칭이 비면 그쪽은 안 그린다 — 셋을 늘 그려 놓고 둘이 같은 모델이면 고르는 시늉이다.
 * ★ 판 K 전에는 별칭뿐이었고 운영에 안 정해 둬서 **고르기가 영영 안 떴다**(사용자 지적).
 *   이제 회사가 지정한 연결이 그 자리를 채운다.
 */
export async function listAiModelOptions(): Promise<AiModelOptions> {
  const res = await apiClient.get<{
    aliases: string[];
    choices: Array<{ key: string; label: string }> | null;
    defaultKey: string | null;
  }>('/assist/models');
  return {
    aliases: Array.isArray(res.data?.aliases) ? res.data.aliases : [],
    /* ★ `null` 은 「못 읽었다」다 — 「고를 것이 없다」(`[]`)와 다른 사실이라 그대로 나른다. */
    choices: Array.isArray(res.data?.choices) ? res.data.choices : null,
    defaultKey: res.data?.defaultKey ?? null,
  };
}

/**
 * 판 L — **내가** 얼마나 썼나. AI 레일 바닥에 적는다.
 *
 * ★★★ 판 K 에서는 «회사» 총량이었다. 「회사 관리자, 사용자 모두 AI 메뉴에는 **개인 사용량**이
 *   나와야 한다」(2026-09-10 사용자)로 바뀌었다 — 회사 총량은 통합설정의 몫이다.
 */
export interface AiUsageSummary {
  todayTokens: number;
  periodTokens: number;
  calls: number;
  days: number;
  /** 날짜별 — 레일에 작은 추이를 그린다. 안 쓴 날은 «줄이 없다». */
  series: Array<{ bucket: string; tokens: number; calls: number }>;
  /**
   * 판 M — 내 한 달 한도. `null` = 못 읽었다(무제한이 아니다 — 화면이 가려서 말한다).
   * ★★★ 넘어도 **막지 않는다** — 계량이 CLI 를 못 재기 때문(2026-09-10 결정).
   */
  quota: AiQuotaState | null;
}

export interface AiQuotaState {
  /** 한 달 상한. `null` = 무제한. */
  limit: string | null;
  source: 'user' | 'company-default' | 'none';
  usedThisMonth: number;
  /** 남은 양. 무제한이면 null. 넘었으면 0(음수가 아니다). */
  remaining: number | null;
  over: boolean;
  /** 0~999. 무제한이면 null — 막대를 안 그린다. */
  percent: number | null;
  month: string;
}

export async function fetchAiUsageSummary(days = 30): Promise<AiUsageSummary> {
  const { data } = await apiClient.get<AiUsageSummary>('/assist/usage', { params: { days } });
  return data;
}
